import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { formatClassMoney, fetchStudentTallyAtLocation } from '../utils/classBookApi';
import { isoDateToLongDisplay } from '../utils/dateFormat';

function formatReasonLabel(reason) {
  const value = String(reason || '').trim().toLowerCase();
  if (value === 'tshirts') return 'T-shirts';
  if (value === 'hoodies') return 'Hoodies';
  if (value === 'blocks') return 'Blocks';
  if (value === 'insurance') return 'Insurance';
  return reason ? String(reason) : 'Other';
}

function TallySection({ title, totalLabel, totalValue, currency, formatAsMoney = true, children, styles }) {
  const displayTotal =
    totalValue == null || totalValue === ''
      ? '—'
      : formatAsMoney
        ? formatClassMoney(totalValue, currency)
        : String(totalValue);
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {totalLabel ? (
        <Text style={styles.sectionTotal}>
          {totalLabel}: {displayTotal}
        </Text>
      ) : null}
      {children}
    </View>
  );
}

function monthGroupKey(isoDate) {
  const match = String(isoDate || '').trim().match(/^(\d{4})-(\d{2})/);
  if (!match) return 'Unknown';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
  return date.toLocaleString(undefined, { month: 'long', year: 'numeric' });
}

function groupItemsByMonth(items, getDateIso) {
  const groups = new Map();
  for (const item of items) {
    const key = monthGroupKey(getDateIso(item));
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  return Array.from(groups.entries());
}

function GroupedDetailList({ groups, emptyText, renderItem, styles }) {
  if (!groups?.length) {
    return <Text style={styles.emptyDetailText}>{emptyText}</Text>;
  }
  return (
    <View style={styles.detailList}>
      {groups.map(([monthLabel, items]) => (
        <View key={monthLabel} style={styles.monthGroup}>
          <Text style={styles.monthGroupTitle}>
            {monthLabel} ({items.length})
          </Text>
          {items.map((item, index) => (
            <View key={item.key ?? `${monthLabel}-${index}`} style={styles.detailRow}>
              {renderItem(item)}
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}

export default function ClassBookStudentTallyModal({
  visible,
  onClose,
  locationId,
  studentName,
  classDate,
  locationLabel,
}) {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tally, setTally] = useState(null);

  useEffect(() => {
    if (!visible || locationId == null || !studentName) {
      setTally(null);
      setError('');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    void fetchStudentTallyAtLocation(locationId, studentName, { classDate })
      .then((data) => {
        if (!cancelled) setTally(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setTally(null);
          setError(err?.message || 'Could not load student tally.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible, locationId, studentName, classDate]);

  const currency = tally?.currency || 'EUR';

  const classDateGroups = useMemo(
    () =>
      groupItemsByMonth(
        (tally?.class_dates || []).map((dateIso) => ({ key: dateIso, dateIso })),
        (item) => item.dateIso
      ),
    [tally?.class_dates]
  );

  const paymentGroups = useMemo(
    () =>
      groupItemsByMonth(
        (tally?.payments || []).map((payment) => ({
          key: `payment-${payment.id}`,
          payment,
        })),
        (item) => item.payment.paid_at
      ),
    [tally?.payments]
  );

  const extraPaymentGroups = useMemo(
    () =>
      groupItemsByMonth(
        (tally?.extra_payments || []).map((entry) => ({
          key: `extra-${entry.id}`,
          entry,
        })),
        (item) => item.entry.paid_at
      ),
    [tally?.extra_payments]
  );

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.overlay} onPress={onClose}>
        <Pressable
          style={styles.sheet}
          onPress={(event) => event.stopPropagation?.()}
          {...(Platform.OS === 'web' ? { dataSet: { classBookStudentTallyModal: 'true' } } : {})}
        >
          <TouchableOpacity style={styles.closeBtn} onPress={onClose}>
            <Text style={styles.closeBtnText}>✕</Text>
          </TouchableOpacity>

          <Text style={styles.title}>Student Tally</Text>
          <Text style={styles.subtitle}>{studentName}</Text>
          {locationLabel ? <Text style={styles.metaText}>{locationLabel}</Text> : null}
          {tally?.year ? (
            <Text style={styles.metaText}>Year {tally.year}</Text>
          ) : null}

          {loading ? (
            <ActivityIndicator
              style={styles.loader}
              color={theme.colors.primary || '#d4af37'}
            />
          ) : error ? (
            <Text style={styles.errorText}>{error}</Text>
          ) : tally ? (
            <ScrollView style={styles.scroll} showsVerticalScrollIndicator>
              <TallySection
                title="Total Classes for the year"
                totalLabel="Count"
                totalValue={tally.total_classes}
                currency={currency}
                formatAsMoney={false}
                styles={styles}
              >
                <GroupedDetailList
                  styles={styles}
                  emptyText="No class dates recorded for this year."
                  groups={classDateGroups}
                  renderItem={(item) => (
                    <Text style={styles.detailText}>{isoDateToLongDisplay(item.dateIso)}</Text>
                  )}
                />
              </TallySection>

              <TallySection
                title="Sum Total for that year (owed)"
                totalLabel="Total"
                totalValue={tally.sum_total_owed}
                currency={currency}
                styles={styles}
              >
                <Text style={styles.helperText}>
                  Based on attended classes and the fee rate for each class date.
                </Text>
              </TallySection>

              <TallySection
                title="Total Paid"
                totalLabel="Total"
                totalValue={tally.total_paid}
                currency={currency}
                styles={styles}
              >
                <GroupedDetailList
                  styles={styles}
                  emptyText="No class payments recorded for this year."
                  groups={paymentGroups}
                  renderItem={(item) => (
                    <Text style={styles.detailText}>
                      {isoDateToLongDisplay(item.payment.paid_at)} —{' '}
                      {formatClassMoney(item.payment.amount, item.payment.currency || currency)}
                    </Text>
                  )}
                />
              </TallySection>

              <TallySection
                title="Extra Paid"
                totalLabel="Total"
                totalValue={tally.extra_paid_total}
                currency={currency}
                styles={styles}
              >
                <GroupedDetailList
                  styles={styles}
                  emptyText="No extra payments recorded for this year."
                  groups={extraPaymentGroups}
                  renderItem={(item) => (
                    <Text style={styles.detailText}>
                      {isoDateToLongDisplay(item.entry.paid_at)} —{' '}
                      {formatClassMoney(item.entry.amount, item.entry.currency || currency)} —{' '}
                      {formatReasonLabel(item.entry.reason)}
                    </Text>
                  )}
                />
              </TallySection>
            </ScrollView>
          ) : null}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function getStyles(theme) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.45)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    sheet: {
      width: '100%',
      maxWidth: 560,
      maxHeight: '88%',
      borderRadius: 12,
      padding: 20,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    closeBtn: {
      position: 'absolute',
      top: 10,
      right: 10,
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2,
      backgroundColor: theme.isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
    },
    closeBtnText: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    title: {
      fontSize: 22,
      fontWeight: '700',
      color: theme.colors.heading || theme.colors.text,
      marginBottom: 4,
      paddingRight: 36,
      backgroundColor: 'transparent',
    },
    subtitle: {
      fontSize: 17,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 4,
      backgroundColor: 'transparent',
    },
    metaText: {
      fontSize: 14,
      color: theme.colors.text,
      opacity: 0.75,
      marginBottom: 2,
      backgroundColor: 'transparent',
    },
    loader: {
      marginVertical: 24,
    },
    errorText: {
      fontSize: 14,
      color: '#c62828',
      marginVertical: 16,
      backgroundColor: 'transparent',
    },
    scroll: {
      marginTop: 12,
      maxHeight: 520,
    },
    section: {
      marginBottom: 18,
      paddingBottom: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.colors.heading || theme.colors.text,
      marginBottom: 6,
      backgroundColor: 'transparent',
    },
    sectionTotal: {
      fontSize: 15,
      fontWeight: '600',
      color: theme.colors.primary || '#d4af37',
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    helperText: {
      fontSize: 13,
      color: theme.colors.text,
      opacity: 0.7,
      backgroundColor: 'transparent',
    },
    detailList: {
      gap: 6,
      backgroundColor: 'transparent',
    },
    monthGroup: {
      marginBottom: 10,
      backgroundColor: 'transparent',
    },
    monthGroupTitle: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.colors.heading || theme.colors.text,
      marginBottom: 4,
      backgroundColor: 'transparent',
    },
    detailRow: {
      paddingVertical: 4,
      backgroundColor: 'transparent',
    },
    detailText: {
      fontSize: 14,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    emptyDetailText: {
      fontSize: 13,
      color: theme.colors.text,
      opacity: 0.6,
      fontStyle: 'italic',
      backgroundColor: 'transparent',
    },
  });
}
